/**
 * My Wishlists Page — "All my wishlists" (Paiseh A.dc design refresh)
 * Shows user's wishlists with create/edit functionality
 */

import { useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useWishlists } from '@/hooks/useWishlists'
import { useAdSense } from '@/hooks/useAdSense'
import { WishlistCard } from '@/components/wishlist/WishlistCard'
import { WishlistForm } from '@/components/wishlist/WishlistForm'
import { Modal } from '@/components/common/Modal'
import { LoginButton } from '@/components/auth/LoginButton'
import { WishlistFormData } from '@/types'
import { createWishlist } from '@/services/wishlist.service'
import { analyticsEvents } from '@/utils/analytics'
import toast from 'react-hot-toast'

/** Dashed "create new" tile from the design */
const NewWishlistCard = ({ onClick }: { onClick: () => void }) => (
  <button
    onClick={onClick}
    className="border-2 border-dashed border-[#E0D3C0] rounded-[24px] flex flex-col items-center justify-center min-h-[260px] hover:border-paiseh-orange transition-colors"
  >
    <div className="w-14 h-14 rounded-[18px] bg-paiseh-peach text-paiseh-orange flex items-center justify-center text-[28px]">
      +
    </div>
    <div className="font-display font-bold text-lg mt-3.5 text-ink">New wishlist</div>
    <div className="text-[13.5px] text-paiseh-faint mt-1">Birthday, wedding, anything</div>
  </button>
)

export const MyWishlists = () => {
  const { user, signInWithGoogle, upgrading } = useAuth()
  const { wishlists, loading } = useWishlists()
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  // Load AdSense script for this page
  useAdSense()

  const handleCreateWishlist = async (data: WishlistFormData) => {
    try {
      const result = await createWishlist(data)
      analyticsEvents.wishlistCreated(result.id, data.occasion, 0)
      toast.success(result.message)
      setIsCreateModalOpen(false)
    } catch (error: any) {
      console.error('Error creating wishlist:', error)
      toast.error(error.message || 'Failed to create wishlist')
      throw error
    }
  }

  // If not authenticated, show login prompt
  if (!user) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center font-jakarta">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="w-24 h-24 bg-paiseh-peach rounded-full flex items-center justify-center mx-auto mb-6 text-5xl">
            📝
          </div>
          <h2 className="font-display font-extrabold text-3xl text-ink mb-4">My Wishlists</h2>
          <p className="text-paiseh-muted mb-8">Sign in to create and manage your wishlists</p>
          <div className="flex justify-center">
            <LoginButton />
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream font-jakarta">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-paiseh-orange mx-auto mb-4"></div>
          <p className="text-paiseh-muted">Loading wishlists...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-cream text-ink font-jakarta">
      <main className="max-w-[1180px] mx-auto px-5 sm:px-10 py-14">
        {/* Header */}
        <div className="flex items-end justify-between gap-4 mb-[26px]">
          <div>
            <div className="text-[13px] font-bold text-paiseh-orange uppercase tracking-[0.06em]">
              Your collection
            </div>
            <h1 className="font-display font-extrabold text-[30px] sm:text-[34px] mt-1.5">
              All my wishlists
            </h1>
            <p className="text-[15.5px] text-paiseh-muted mt-2 max-w-[440px]">
              Keep a list for every occasion. Tap one to add items or grab its share link.
            </p>
          </div>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-ink text-cream font-bold text-[15px] px-6 py-3 rounded-full hover:opacity-90 transition-opacity whitespace-nowrap"
          >
            + New wishlist
          </button>
        </div>

        {/* Guest "save your lists" nudge — only once they have something to lose */}
        {user?.isAnonymous && wishlists.length > 0 && (
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-paiseh-peach rounded-[18px] px-5 py-4">
            <div className="flex items-center gap-3 text-[#9C4226]">
              <span className="text-xl">🔐</span>
              <p className="text-[14.5px] font-semibold">
                You&rsquo;re browsing as a guest — these lists live only in this browser.
                Save them so you don&rsquo;t lose them.
              </p>
            </div>
            <button
              onClick={() => signInWithGoogle().catch(() => {})}
              disabled={upgrading}
              className="flex-none bg-ink text-cream font-bold text-sm px-5 py-2.5 rounded-full hover:opacity-90 transition-opacity disabled:opacity-50 whitespace-nowrap"
            >
              {upgrading ? 'Signing in…' : 'Save with Google'}
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[22px]">
          {wishlists.map((wishlist) => (
            <WishlistCard key={wishlist.id} wishlist={wishlist} />
          ))}
          <NewWishlistCard onClick={() => setIsCreateModalOpen(true)} />
        </div>

        {wishlists.length === 0 && (
          <p className="text-center text-paiseh-muted mt-8 text-[15px]">
            No wishlists yet — create your first one to get started. 🎉
          </p>
        )}
      </main>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create New Wishlist"
      >
        <WishlistForm
          onSubmit={handleCreateWishlist}
          onCancel={() => setIsCreateModalOpen(false)}
          submitLabel="Create Wishlist"
        />
      </Modal>
    </div>
  )
}
